
import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '공연',
    date: '',
    location: '',
    people: '',
    message: '',
    privacy: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.privacy) return;
    if (formData.message.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('service', formData.service);
      formBody.append('date', formData.date);
      formBody.append('location', formData.location);
      formBody.append('people', formData.people);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5su12mm4hfebckkvk4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '', phone: '', email: '', service: '공연',
          date: '', location: '', people: '', message: '', privacy: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 lg:py-40 bg-warmWhite">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">

        {/* Section Title */}
        <div className="mb-20 lg:mb-28">
          <p className="text-xs font-medium tracking-widest uppercase text-primary mb-4">Contact</p>
          <h2 className="text-3xl lg:text-5xl font-light text-black leading-tight tracking-tight">
            문의하기
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">

          {/* Contact Info */}
          <div className="lg:col-span-4">
            <div className="space-y-8">
              <div>
                <p className="text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">대표전화</p>
                <a
                  href="tel:010-5529-0122"
                  className="text-2xl lg:text-3xl font-light text-black hover:text-primary transition-colors cursor-pointer"
                >
                  010-5529-0122
                </a>
              </div>

              <div>
                <p className="text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">이메일</p>
                <a
                  href="mailto:kildedee@naver.com"
                  className="text-sm font-light text-black hover:text-primary transition-colors cursor-pointer"
                >
                  kildedee@naver.com
                </a>
              </div>

              <div>
                <p className="text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">카카오톡</p>
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-light text-black hover:text-primary transition-colors cursor-pointer"
                >
                  카카오톡 상담
                </a>
              </div>

              <div className="pt-6 border-t border-lineGray">
                <p className="text-sm text-textGray font-light leading-relaxed">
                  문의 접수 후 24시간 내 연락드립니다.<br />
                  급한 문의는 대표전화로 연락주세요.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-8">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">
                    이름 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-0 py-3 text-sm font-light bg-transparent border-0 border-b border-lineGray focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">
                    연락처 *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="w-full px-0 py-3 text-sm font-light bg-transparent border-0 border-b border-lineGray focus:outline-none focus:border-black transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">
                  이메일 *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-0 py-3 text-sm font-light bg-transparent border-0 border-b border-lineGray focus:outline-none focus:border-black transition-colors"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="service" className="block text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">
                    서비스 *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    required
                    className="w-full px-0 py-3 text-sm font-light bg-transparent border-0 border-b border-lineGray focus:outline-none focus:border-black transition-colors cursor-pointer"
                  >
                    <option value="공연">공연</option>
                    <option value="체험">체험</option>
                    <option value="영상">영상 제작</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="date" className="block text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">
                    희망 일정
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-0 py-3 text-sm font-light bg-transparent border-0 border-b border-lineGray focus:outline-none focus:border-black transition-colors"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="location" className="block text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">
                    장소
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-0 py-3 text-sm font-light bg-transparent border-0 border-b border-lineGray focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="people" className="block text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">
                    예상 인원
                  </label>
                  <input
                    type="text"
                    id="people"
                    name="people"
                    value={formData.people}
                    onChange={(e) => setFormData({ ...formData, people: e.target.value })}
                    className="w-full px-0 py-3 text-sm font-light bg-transparent border-0 border-b border-lineGray focus:outline-none focus:border-black transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-medium tracking-widest uppercase text-textGray mb-2.5">
                  문의 내용 *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-0 py-3 text-sm font-light bg-transparent border-0 border-b border-lineGray focus:outline-none focus:border-black transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-textGray font-light mt-1.5 text-right">
                  {formData.message.length} / 500
                </p>
              </div>

              <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={(e) => setFormData({ ...formData, privacy: e.target.checked })}
                  required
                  className="mt-1 w-4 h-4 cursor-pointer flex-shrink-0"
                />
                <label htmlFor="privacy" className="text-sm text-textGray font-light cursor-pointer leading-relaxed">
                  개인정보 수집 및 이용에 동의합니다
                </label>
              </div>

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-12 py-4 bg-black text-white text-xs font-medium tracking-wider uppercase hover:bg-black/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '전송 중...' : '문의하기'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="p-5 bg-white border border-lineGray">
                  <p className="text-sm text-black font-light">
                    문의가 접수되었습니다. 확인 후 빠르게 연락드리겠습니다.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-5 bg-white border border-primary/30">
                  <p className="text-sm text-primary font-light">
                    문의 전송에 실패했습니다. 다시 시도해주세요.
                  </p>
                </div>
              )}
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}
